import { useRef } from "react"

import type { MimeType } from "@/components/upload/constants"
import type { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { useFileUploadWithValidation } from "@/hooks/useFileUploadWithValidation"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const useHandleFileUpload = ({
  referenceTableName,
  allowedFileTypes,
}: {
  referenceTableName: ReferenceTableName
  allowedFileTypes: MimeType[]
}): {
  uploadFile: (params: { file: File; type: FieldGroupEnum; referenceId: string; label?: string }) => Promise<void>
  onMarkFileAsNeedRemoval: (fileId: string) => void
  removeFiles: () => Promise<void>
} => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const filesToRemoveRef = useRef<string[]>([])
  const { removeSingleFile } = useFilesRemove()
  const { uploadFile: baseUploadFile } = useFileUploadWithValidation({
    referenceTableName,
    allowedFileTypes,
  })

  const uploadFile = async ({
    file,
    type,
    referenceId,
    label,
  }: {
    file: File
    type: FieldGroupEnum
    referenceId: string
    label?: string
  }) => {
    if (!file) {
      return
    }

    try {
      const uploadSuccess = await baseUploadFile(file, type, referenceId, label)
      if (!uploadSuccess) {
        throw new Error(`Unable to upload file: ${file.name}. Please try again.`)
      }
    } catch (error) {
      notify({
        message: error instanceof Error ? error.message : "Unable to upload file. Please try again.",
        type: NotificationType.Error,
      })
    }
  }

  const removeFiles = async () => {
    if (filesToRemoveRef.current.length === 0) {
      return
    }

    for (const fileId of filesToRemoveRef.current) {
      try {
        await removeSingleFile(fileId)
      } catch (err) {
        console.log(err)
      }
    }
    filesToRemoveRef.current = []
  }

  const onMarkFileAsNeedRemoval = (fileId: string) => {
    if (!filesToRemoveRef.current.includes(fileId)) {
      filesToRemoveRef.current.push(fileId)
    }
  }

  return {
    uploadFile,
    onMarkFileAsNeedRemoval,
    removeFiles,
  }
}

export default useHandleFileUpload
